import { isBefore, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import type { AppointmentWithRelations } from '@vagusx/clients';
import { fromIsoDate } from '@/lib/date-range';

/** The time a staff member picked on the reschedule calendar. */
export interface RescheduleTarget {
  /** ISO instant. */
  startTime: string;
  providerId: string;
  providerName: string;
  locationId: string;
  locationName: string;
  /** The clinic's zone: the one the calendar column showed this time in. */
  timezone: string;
  /** The open slot's type, or null for an empty calendar cell. */
  appointmentTypeId: string | null;
  /** The calendar block that was clicked (an open slot, or an empty cell's id). */
  slotId?: string;
}

type PlannedAppointment = Pick<
  AppointmentWithRelations,
  'providerId' | 'locationId' | 'startTime' | 'endTime' | 'appointmentTypeId'
>;

/**
 * How a pick is carried out:
 *   - move: same provider AND location, so the EHR moves the appointment in
 *     place (a native reschedule, ADR-092).
 *   - rebook: a different provider or location. The EHR can't move an
 *     appointment across them, so the new time is booked first and the original
 *     cancelled after, keeping the original's length and type. The slot's own
 *     type is only its template (the calendar shows every type), so it is used
 *     only when the appointment has none.
 *   - unchanged: the pick IS the appointment's current time and place.
 */
export type ReschedulePlan =
  | { kind: 'move' }
  | { kind: 'rebook'; durationMinutes: number; appointmentTypeId: string | undefined }
  | { kind: 'unchanged' };

export function planReschedule(
  appointment: PlannedAppointment,
  target: RescheduleTarget
): ReschedulePlan {
  const sameColumn =
    target.providerId === appointment.providerId && target.locationId === appointment.locationId;
  if (!sameColumn) {
    return {
      kind: 'rebook',
      durationMinutes: appointmentMinutes(appointment),
      appointmentTypeId: appointment.appointmentTypeId ?? target.appointmentTypeId ?? undefined,
    };
  }
  const sameTime =
    new Date(target.startTime).getTime() === new Date(appointment.startTime).getTime();
  return sameTime ? { kind: 'unchanged' } : { kind: 'move' };
}

/** The appointment's length in minutes, floored at the 5 the booking API accepts. */
export function appointmentMinutes(
  appointment: Pick<PlannedAppointment, 'startTime' | 'endTime'>
): number {
  const ms = new Date(appointment.endTime).getTime() - new Date(appointment.startTime).getTime();
  return Math.max(5, Math.round(ms / 60000));
}

/**
 * The calendar day the reschedule view opens on: the appointment's own day AT
 * ITS CLINIC (a Guam 8 AM is that morning there, the previous evening in
 * California), or the clinic's today when that day has passed. Returned as a
 * local-midnight Date, the shape the day-slots query reads as "that day".
 */
export function initialRescheduleDay(
  appointmentStartIso: string,
  timezone: string | null | undefined,
  now: Date = new Date()
): Date {
  const zone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const clinicDay = fromIsoDate(formatInTimeZone(appointmentStartIso, zone, 'yyyy-MM-dd'));
  const today = clinicToday(zone, now);
  if (!clinicDay || isBefore(clinicDay, today)) return today;
  return clinicDay;
}

/**
 * Today at the clinic, as a local-midnight Date. A calendar day is the clinic's
 * day, so "today" is too: in Guam it is already tomorrow for a California desk.
 */
export function clinicToday(timezone: string | null | undefined, now: Date = new Date()): Date {
  const zone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  return fromIsoDate(formatInTimeZone(now, zone, 'yyyy-MM-dd')) ?? startOfDay(now);
}

/** The part of a calendar block {@link overlapsBooking} reads. */
interface CalendarBlock {
  status: 'open' | 'booked' | 'unavailable';
  startTime: string;
  endTime: string;
  appointment: { id: string } | null;
}

/**
 * Whether the appointment, at `startIso` for `durationMinutes`, would overlap
 * another booking in the column. The appointment being moved is ignored: it
 * gives its own time up. A column's open slot or empty time can be shorter than
 * the appointment, so a pick that looks free can still run into the next patient.
 */
export function overlapsBooking(
  startIso: string,
  durationMinutes: number,
  blocks: readonly CalendarBlock[],
  movingAppointmentId: string
): boolean {
  const start = new Date(startIso).getTime();
  const end = start + durationMinutes * 60000;
  return blocks.some(
    (b) =>
      b.status === 'booked' &&
      b.appointment?.id !== movingAppointmentId &&
      new Date(b.startTime).getTime() < end &&
      new Date(b.endTime).getTime() > start
  );
}

/** The part of an open calendar slot {@link slotRefusesType} reads. */
interface TypedBlock {
  status: 'open' | 'booked' | 'unavailable';
  acceptedTypeIds: readonly string[] | null;
}

/**
 * Whether an open slot is known not to take the appointment's type: it lists the
 * types it takes and this one is not among them. The EHR refuses such a booking,
 * and so does the move endpoint (ADR-313), so the calendar marks the slot instead
 * of letting staff pick it. Unknown types, or an appointment with no type, may.
 */
export function slotRefusesType(
  block: TypedBlock,
  appointmentTypeId: string | null | undefined
): boolean {
  return (
    block.status === 'open' &&
    !!appointmentTypeId &&
    block.acceptedTypeIds !== null &&
    !block.acceptedTypeIds.includes(appointmentTypeId)
  );
}
